import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import {
  useAddNoteTopic,
  useGetTopicDetails,
  useUpdateNote,
  useUpdateVisibility,
  useGetPermissions,
  useUpdateTopic,
} from '@/hooks/api/dashboard'
import { useLocalStorage } from '@/hooks/common'
import { Topic } from '@/types'
import { PrivacyType, ResourceType, TopicType } from '@/utils/constants'
import { IMAGES } from '@/assets/images'
import { fireAlertToast } from '@/design-system/alerts'
import { useGallerySourceAlbum, SourceAlbumRef } from '@/features/gallery-studio/hooks/useGallerySourceAlbum'
import { parseSourceAlbumIds, withSourceAlbumIds } from '@/features/gallery-studio/utils/galleryXData'
import { StudioAssetPhoto, StudioCameraState, StudioPin, StudioRoomState, StudioSceneState } from '../types'
import { ROOM_DEFAULTS, STUDIO3D_NOTE_NAME, STUDIO3D_NOTE_TYPE, STUDIO_PIN_NOTE_TYPE } from '../constants'
import { pickLaunchClassicTemplate } from '../classicCatalog'
import { layoutClassicArts } from '../utils/classicAutoLayout'
import { convertLegacyClassicGallery } from '../utils/legacyClassicConverter'
import type { StudioArt } from '../types'

/** Exported for `usePublicGallery` — the guest route reads the same saved-scene note through the same anonymous topic fetch shape. */
export function parseSceneNote(topic: Topic | null): StudioSceneState | null {
  const note = topic?.notes.find((n) => n.noteName === STUDIO3D_NOTE_NAME)
  if (!note) return null
  try {
    const parsed = JSON.parse(note.noteText) as StudioSceneState
    if (parsed?.version === 1 && Array.isArray(parsed.arts)) return parsed
    return null
  } catch {
    return null
  }
}

/** Local id scheme used before pins had real notes backing them — see `parsePinNotes`. */
export const isLegacyPinId = (id: string) => id.startsWith('pin-')

/**
 * Story pins as real Notes on the gallery topic (`STUDIO_PIN_NOTE_TYPE`) —
 * one note per pin, so add/edit/delete are independent backend writes
 * instead of all sharing one full-blob overwrite. Exported for
 * `usePublicGallery` — same anonymous topic fetch carries these notes too.
 */
export function parsePinNotes(topic: Topic | null): StudioPin[] {
  if (!topic) return []
  const pins: StudioPin[] = []
  for (const note of topic.notes) {
    if (note.noteType !== STUDIO_PIN_NOTE_TYPE) continue
    try {
      const data = JSON.parse(note.noteText) as Omit<StudioPin, 'id'>
      pins.push({ ...data, id: note.noteID })
    } catch {
      // Skip a malformed pin note rather than fail the whole scene load.
    }
  }
  return pins
}

/** Scene note payload — pins are no longer part of this (see `parsePinNotes`), only room/arts/camera. */
export type ScenePersistState = { room: StudioRoomState; arts: StudioArt[]; camera?: StudioCameraState | null }

export interface UseStudio3DDataResult {
  loading: boolean
  /** True once it's safe to boot the Babylon scene with a final seed — waits on the source album too, unless a saved scene already covers it. */
  seedReady: boolean
  isError: boolean
  topic: Topic | null
  isOwner: boolean
  galleryTitle: string
  initialRoom: StudioRoomState
  initialArts: StudioArt[]
  initialPins: StudioPin[]
  /** Last-saved camera pose, if any — see StudioCameraState. */
  initialCamera: StudioCameraState | null
  sourcePhotos: StudioAssetPhoto[]
  hasSourcePhotos: boolean
  sourcePhotosLoading: boolean
  /** True exactly when this load fell through to the bundled placeholder seed below — no saved scene note, no legacy Classic data, no source album at all. Drives StudioFirstRunOverlay (Option D's "land, then ask"). */
  isNewGallery: boolean
  saving: boolean
  savedVersion: number
  persist: (state: ScenePersistState) => void
  /** Write any pending debounced scene note immediately (route leave / hide). */
  flushPersist: () => void
  renameGallery: (name: string) => void
  /** Sets/replaces the primary linked album (First Run / wizard path). */
  setSourceAlbum: (albumId: string) => void
  /** Adds another album to the linked set (multi-album Assets palette). */
  addSourceAlbum: (albumId: string) => void
  /** Unlinks an album from the set. */
  removeSourceAlbum: (albumId: string) => void
  linkedAlbums: SourceAlbumRef[]
  sourceAlbumIds: string[]
  /** Re-fetch linked album photos (e.g. after Assets panel upload). */
  refreshSourcePhotos: () => void
  shareLink: string
  isPrivate: boolean
  togglePrivacy: () => void
  goBack: () => void
}

/**
 * Loads the GALLERY topic + its seeded source-album photos, resolves a
 * starting scene (existing `studio3dSceneStateV1` note > auto-laid-out real
 * photos > bundled placeholder demo), and persists edits back to a new note
 * on the same topic — see constants.ts for why this is a new noteType
 * instead of reusing the legacy PatternNoteTypes.
 */
export function useStudio3DData(galleryId: string): UseStudio3DDataResult {
  const navigate = useNavigate()
  const [ewUser] = useLocalStorage('ewUser', null)
  const getTopicDetails = useGetTopicDetails()
  const addNoteTopic = useAddNoteTopic()
  const updateNote = useUpdateNote()
  const getPermissions = useGetPermissions()
  const updateVisibility = useUpdateVisibility()
  const updateTopic = useUpdateTopic(galleryId)

  const [topic, setTopic] = useState<Topic | null>(null)
  const [loading, setLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savedVersion, setSavedVersion] = useState(0)

  const sceneNoteRef = useRef<{ noteID: string } | null>(null)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const renameTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingPersistRef = useRef<ScenePersistState | null>(null)
  const lastSavedJsonRef = useRef<string | null>(null)
  const persistInFlightRef = useRef(false)
  const persistQueuedRef = useRef<ScenePersistState | null>(null)

  const load = useCallback(async () => {
    if (!galleryId) return
    setLoading(true)
    try {
      const topicData = await getTopicDetails.mutateAsync({ topicID: galleryId })
      setTopic(topicData)
      const note = topicData.notes.find((n) => n.noteName === STUDIO3D_NOTE_NAME)
      if (note) sceneNoteRef.current = { noteID: note.noteID }
    } catch {
      setIsError(true)
    } finally {
      setLoading(false)
    }
  }, [galleryId, getTopicDetails])

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galleryId])

  const isOwner = useMemo(
    () => !!ewUser?.userID && !!topic?.userID && String(ewUser.userID) === String(topic.userID),
    [ewUser, topic],
  )

  const {
    photos: sourcePhotos,
    loading: sourcePhotosLoading,
    sourceAlbumId,
    sourceAlbumIds,
    linkedAlbums,
    refresh: refreshSourcePhotos,
  } = useGallerySourceAlbum(topic)

  // `useAlbumDetail`'s `loading` flag is seeded from the *first* render's
  // `sourceAlbumId` (which is still `undefined` while the gallery topic itself
  // is loading), so there's exactly one tick — right when the topic finishes
  // loading and `sourceAlbumId` first resolves to a real id — where it reads
  // as "not loading" despite the album fetch not having started yet. Gating
  // `seedReady` on that raw flag would boot the scene on the placeholder demo
  // and never recover (Studio's `initialArts` only seeds once). Instead, only
  // trust "not loading" once we've actually observed a loading pass for this
  // album id.
  const albumFetchStartedRef = useRef(false)
  useEffect(() => {
    if (sourcePhotosLoading) albumFetchStartedRef.current = true
  }, [sourcePhotosLoading])
  const photosSettled = !sourceAlbumId || albumFetchStartedRef.current ? !sourcePhotosLoading : false

  const savedScene = useMemo(() => parseSceneNote(topic), [topic])
  // Skip no-op autosaves when the live document still matches the loaded note.
  useEffect(() => {
    if (savedScene) lastSavedJsonRef.current = JSON.stringify(savedScene)
  }, [savedScene])
  // First visit ever to this Studio for a gallery built entirely in the old
  // pattern editor — no `studio3dSceneStateV1` note exists yet, so without
  // this the seed below would fall through to `ROOM_DEFAULTS` (procedural)
  // and silently discard the gallery's real Classic room/photos/frame.
  const legacyClassicScene = useMemo(() => (savedScene ? null : convertLegacyClassicGallery(topic)), [savedScene, topic])

  // Real pin notes on the topic — always the base layer, regardless of which
  // room/arts branch below applies.
  const pinNotes = useMemo(() => parsePinNotes(topic), [topic])

  const seed = useMemo(() => {
    // Merge onto ROOM_DEFAULTS so older notes missing ambience/fog/scenePreset
    // still get a full room — and so a partial room object can't leave those
    // fields undefined in the inspector after refresh.
    if (savedScene) {
      return {
        room: { ...ROOM_DEFAULTS, ...savedScene.room },
        arts: savedScene.arts,
        // Legacy pins still embedded in the scene blob (pre-migration saves)
        // plus real pin notes — see `isLegacyPinId` for how these get
        // upgraded off the blob once touched.
        pins: [...pinNotes, ...(savedScene.pins || [])],
        camera: savedScene.camera || null,
      }
    }
    if (legacyClassicScene) {
      return {
        room: { ...ROOM_DEFAULTS, ...legacyClassicScene.room },
        arts: legacyClassicScene.arts,
        pins: [...pinNotes, ...(legacyClassicScene.pins || [])],
        camera: null,
      }
    }
    if (sourcePhotos.length > 0) {
      // Task #17: Classic is the launch default for every brand-new seed
      // (blank gallery once an album lands, or "Turn into 3D" from an
      // album) — Studio's procedural corridor/loft/pavilion stay reachable
      // from RoomInspector only for galleries already in Studio mode; see
      // that component's mode-tab gating.
      const templateId = pickLaunchClassicTemplate(sourcePhotos.length)
      return {
        room: { ...ROOM_DEFAULTS, mode: 'classic' as const, classicTemplateId: templateId },
        arts: layoutClassicArts(sourcePhotos, templateId),
        pins: pinNotes,
        camera: null,
      }
    }
    const placeholderTemplateId = pickLaunchClassicTemplate(2)
    return {
      room: { ...ROOM_DEFAULTS, mode: 'classic' as const, classicTemplateId: placeholderTemplateId },
      arts: layoutClassicArts(
        [
          { id: 'placeholder-1', url: IMAGES.topicPreviewPlaceholder, egoId: undefined },
          { id: 'placeholder-2', url: IMAGES.topicPreviewPlaceholder, egoId: undefined },
        ],
        placeholderTemplateId,
      ),
      pins: pinNotes,
      camera: null,
    }
    // Only re-seed when the topic's saved note or its source album identity changes —
    // not on every sourcePhotos array re-fetch (would reset in-progress live edits).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedScene, legacyClassicScene, pinNotes, topic?.topicID, sourcePhotos.length > 0])

  const writeSceneNote = useCallback(
    async (state: ScenePersistState) => {
      if (!topic) return
      const noteText = JSON.stringify({ version: 1, ...state, camera: state.camera ?? undefined } satisfies StudioSceneState)
      if (lastSavedJsonRef.current === noteText) {
        setSaving(false)
        return
      }
      if (persistInFlightRef.current) {
        persistQueuedRef.current = state
        return
      }
      persistInFlightRef.current = true
      setSaving(true)
      try {
        if (sceneNoteRef.current) {
          await updateNote.mutateAsync({ noteID: sceneNoteRef.current.noteID, topicID: topic.topicID, noteText })
        } else {
          const response = await addNoteTopic.mutateAsync({
            topicID: topic.topicID,
            noteName: STUDIO3D_NOTE_NAME,
            noteText,
            noteType: STUDIO3D_NOTE_TYPE,
            egoItems: [],
          })
          if (response?.noteID) sceneNoteRef.current = { noteID: response.noteID }
        }
        lastSavedJsonRef.current = noteText
        setSavedVersion((v) => v + 1)
      } catch {
        toast.error('Failed to save — retrying on your next edit')
      } finally {
        persistInFlightRef.current = false
        setSaving(false)
        const queued = persistQueuedRef.current
        persistQueuedRef.current = null
        if (queued) {
          void writeSceneNote(queued)
        }
      }
    },
    [topic, addNoteTopic, updateNote],
  )

  const persist = useCallback(
    (state: ScenePersistState) => {
      if (!topic) return
      pendingPersistRef.current = state
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      setSaving(true)
      saveTimerRef.current = setTimeout(() => {
        saveTimerRef.current = null
        const pending = pendingPersistRef.current
        pendingPersistRef.current = null
        if (pending) void writeSceneNote(pending)
      }, 900)
    },
    [topic, writeSceneNote],
  )

  const flushPersist = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }
    const pending = pendingPersistRef.current
    pendingPersistRef.current = null
    if (pending) void writeSceneNote(pending)
  }, [writeSceneNote])

  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') flushPersist()
    }
    document.addEventListener('visibilitychange', onHide)
    window.addEventListener('pagehide', flushPersist)
    return () => {
      document.removeEventListener('visibilitychange', onHide)
      window.removeEventListener('pagehide', flushPersist)
      flushPersist()
    }
  }, [flushPersist])

  const isPrivate = topic?.visibility === PrivacyType.PRIVATE.toString()

  const togglePrivacy = useCallback(async () => {
    if (!topic) return
    const newPrivacy = isPrivate ? PrivacyType.PUBLIC : PrivacyType.PRIVATE
    try {
      const permData = await getPermissions.mutateAsync(topic.topicID)
      await updateVisibility.mutateAsync({
        resourceID: topic.topicID,
        cliqueTypes: topic.cliqueTypes,
        cliques: [],
        connections: [],
        accessTypeID: newPrivacy.toString(),
        permissionID: permData.permissionID,
        resourceType: ResourceType.TOPIC,
      })
      setTopic((prev) => (prev ? { ...prev, visibility: newPrivacy.toString() } : prev))
      fireAlertToast(newPrivacy === PrivacyType.PUBLIC ? 'share.madePublic' : 'share.madePrivate')
    } catch {
      fireAlertToast('share.privacyUpdateFailed')
    }
  }, [topic, isPrivate, getPermissions, updateVisibility])

  const renameGallery = useCallback(
    (name: string) => {
      if (!topic) return
      const previousName = topic.topicName
      // Optimistic — the room inspector's title input is controlled off
      // `topic.topicName`, so it needs to reflect every keystroke immediately.
      // The actual API call is debounced below so we don't fire one request
      // per keystroke (mirrors `persist()`'s debounce for the same reason).
      setTopic((prev) => (prev ? { ...prev, topicName: name } : prev))
      if (renameTimerRef.current) clearTimeout(renameTimerRef.current)
      renameTimerRef.current = setTimeout(() => {
        const trimmed = name.trim()
        if (!trimmed || trimmed === previousName) return
        // Pass the topic's current topicXData through unchanged — the backend
        // update overwrites it wholesale (see UpdateTopicParams' doc comment),
        // so omitting it here would silently sever sourceAlbumId/wizard data.
        updateTopic.mutate(
          { topicName: trimmed, topicXData: topic.topicXData || '' },
          {
            onError: () => {
              toast.error('Failed to rename — try again')
              setTopic((prev) => (prev ? { ...prev, topicName: previousName } : prev))
            },
          },
        )
      }, 900)
    },
    [topic, updateTopic],
  )

  const writeSourceAlbumIds = useCallback(
    (albumIds: string[], errorMsg: string) => {
      if (!topic) return
      const nextXData = withSourceAlbumIds(topic.topicXData, albumIds)
      setTopic((prev) => (prev ? { ...prev, topicXData: nextXData } : prev))
      updateTopic.mutate(
        { topicName: topic.topicName, topicXData: nextXData },
        { onError: () => toast.error(errorMsg) },
      )
    },
    [topic, updateTopic],
  )

  const setSourceAlbum = useCallback(
    (albumId: string) => {
      // First Run / primary link — becomes the sole linked album (or primary of one).
      writeSourceAlbumIds([albumId], "Couldn't link that album — try again from Assets")
    },
    [writeSourceAlbumIds],
  )

  const addSourceAlbum = useCallback(
    (albumId: string) => {
      if (!topic) return
      const current = parseSourceAlbumIds(topic.topicXData)
      if (current.includes(albumId)) {
        toast('Album already linked', { icon: '◈' })
        return
      }
      writeSourceAlbumIds([...current, albumId], "Couldn't add that album — try again")
    },
    [topic, writeSourceAlbumIds],
  )

  const removeSourceAlbum = useCallback(
    (albumId: string) => {
      if (!topic) return
      const next = parseSourceAlbumIds(topic.topicXData).filter((id) => id !== albumId)
      writeSourceAlbumIds(next, "Couldn't unlink that album — try again")
    },
    [topic, writeSourceAlbumIds],
  )

  // `/g/:id` is the real public guest route (see usePublicGallery +
  // GuestGalleryScreen) — unauthenticated, mirrors the album guest pattern
  // (`/a/:shareId`). Replaces the old `/preview?topicID=...` link, which
  // sat behind the authenticated dashboard shell and bounced signed-out
  // guests to /login despite "Make public" claiming otherwise.
  const shareLink = useMemo(() => {
    if (!topic) return ''
    return `${window.location.origin}/g/${topic.topicID}`
  }, [topic])

  const goBack = useCallback(() => navigate('/galleries'), [navigate])

  const seedReady = !loading && !isError && (!!savedScene || photosSettled)
  const isNewGallery = !loading && !savedScene && !legacyClassicScene && !sourceAlbumId

  return {
    loading,
    seedReady,
    isError: isError || (topic ? topic.topicType !== TopicType.GALLERY.toString() : false),
    topic,
    isOwner,
    galleryTitle: topic?.topicName || 'Untitled gallery',
    initialRoom: seed.room,
    initialArts: seed.arts,
    initialPins: seed.pins,
    initialCamera: seed.camera,
    sourcePhotos: sourcePhotos.map((p) => ({
      id: p.id,
      url: p.url,
      thumbUrl: p.thumbUrl,
      egoId: p.egoId,
      albumId: p.albumId,
      albumTitle: p.albumTitle,
    })),
    hasSourcePhotos: sourcePhotos.length > 0,
    sourcePhotosLoading,
    isNewGallery,
    saving,
    savedVersion,
    persist,
    flushPersist,
    renameGallery,
    setSourceAlbum,
    addSourceAlbum,
    removeSourceAlbum,
    linkedAlbums,
    sourceAlbumIds,
    refreshSourcePhotos,
    shareLink,
    isPrivate,
    togglePrivacy,
    goBack,
  }
}
